import { useAppSelector } from "app/hooks"
import { GenericCardInfos } from "components/display/Card/CardInfos"
import { createIcon } from "components/icons/IconFactory"
import { CardsSection } from "components/layout/Section/CardsSection"
import { NsKey } from "enums/NsKey"
import { useTranslation } from "react-i18next"
import { ReferentialKey } from "api/referential"
import { selectReferentialDataByKey } from "features/referentials/selectors/selectReferentialDataByKey"
import { useCallback, useMemo, useState } from "react"
import { useBorrowerRiskData } from "features/subscription-process/contexts/selectors/selectors"
import { AddIcon } from "components/icons/Icons"
import { LightAction } from "components/ui/LightAction"
import { useDialog } from "hooks/useDialog"
import { Sizes } from "enums/sizes"
import { BorrowerProjectQuestion } from "./BorrowerProjectQuestion"
import { useParams } from "react-router-dom"
import { UpdateBorrowerProjectCommand } from "api/borrower"
import { borrowersAPI } from "services/swagger-api"
import { useSubscriptionProcessActions } from "features/subscription-process/contexts/SubscriptionProcessContext"
import { Calendar } from "lucide-react"

export const BorrowerProfilingProjectSection = () => {
  const { t } = useTranslation(NsKey.Borrower)
  const { openBlankDialog, closeDialog } = useDialog()
  const { borrowerProjectId } = useParams<{ borrowerProjectId: string }>();

  const [isLoading, setIsLoading] = useState(false)

  const riskData = useBorrowerRiskData()
  const project = riskData?.project
  const { refreshSubscriptionProcess } = useSubscriptionProcessActions()
  const insuranceProjectReferential = useAppSelector(selectReferentialDataByKey(ReferentialKey.InsuranceProject))
  const projectTypeReferential = useAppSelector(selectReferentialDataByKey(ReferentialKey.ProjectType))

  const PrimaryResidence = createIcon('commercial');

  const reloadBorrowerSummary = useCallback(() => {
    refreshSubscriptionProcess()
  }, [refreshSubscriptionProcess])

  const renderIcon = useCallback((iconKey?: string) => {
    if (!iconKey) {
      return <></>
    }
    const Icon = createIcon(iconKey)
    return <div className="p-4 border border-primary/50 rounded-xl bg-primary/10">
      <Icon className="text-primary w-11 h-11" />
    </div>
  }, [])

  const upsertBorrowerProject = useCallback(async (projectType: string, insuranceProject: string, startDate: string) => {
    try {
      setIsLoading(true)
      if (borrowerProjectId) {
        const updateBorrowerProjectCommand: UpdateBorrowerProjectCommand = {
          projectTypeId: projectType,
          insuranceProjectId: insuranceProject,
          projectStartDate: new Date(startDate),
          projectName: 'Default'
        }
        const response = await borrowersAPI.updateBorrowerProject({ borrowerProjectId, updateBorrowerProjectCommand })
        if (!response.isSuccess) {
          throw new Error(response.errorMessage || 'There is an error')
        }
        closeDialog()
        reloadBorrowerSummary()
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [borrowerProjectId, closeDialog, reloadBorrowerSummary])

  const openProfilingQuestions = useCallback(() => {
    openBlankDialog({
      title: 'Description du projet',
      size: Sizes["5xl"]
    },
      <BorrowerProjectQuestion upsertBorrowerProject={upsertBorrowerProject} />
    )
  }, [openBlankDialog, upsertBorrowerProject,])

  const projectType = useMemo(() => {
    if (!project?.projectTypeId)
      return undefined
    return projectTypeReferential && projectTypeReferential.find(item => item.id === project.projectTypeId)
  }, [project?.projectTypeId, projectTypeReferential])

  const insuranceProject = useMemo(() => {
    if (!project?.insuranceProjectId)
      return undefined
    return insuranceProjectReferential && insuranceProjectReferential.find(item => item.id === project.insuranceProjectId)
  }, [project?.insuranceProjectId, insuranceProjectReferential])

  const projectAnswered = useMemo(() => {
    return projectType && insuranceProject
  }, [insuranceProject, projectType])

  const formattedStartDate = useMemo(() => {
    if (!project?.projectStartDate) return null
    return new Date(project.projectStartDate).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })
  }, [project?.projectStartDate])

  return (
    <CardsSection
      icon={<PrimaryResidence className="h-8 w-8 text-primary" />}
      title={t("BORROWER.LABELS.LOAN_SITUATION")}
      titleAside={projectAnswered && <LightAction icon={AddIcon} label={t("BORROWER.LABELS.FILL_OUT_SURVEY")} onClick={() => openProfilingQuestions()} />}
    >
      {
        !projectAnswered &&
        <div className="flex items-center justify-center bg-white w-full rounded-xl border p-8">
          <LightAction icon={AddIcon} label={t("BORROWER.LABELS.FILL_OUT_SURVEY")} onClick={() => openProfilingQuestions()} />
        </div>
      }
      {projectAnswered && (
        <GenericCardInfos>
          <div className="grid grid-cols-3 gap-6">
            <div className="flex items-start gap-4">
              {renderIcon(projectType?.iconKey)}
              <div className="flex flex-col gap-2 items-start">
                <span className="text-sm font-bold text-zinc-800">
                  {t("BORROWER.LABELS.PROJECT_TYPE")}
                </span>
                <span className="text-sm text-primary font-medium bg-primary/15 px-3 py-1 rounded-full">
                  {projectType && projectType.label}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-4">
              {renderIcon(insuranceProject?.iconKey)}
              <div className="flex flex-col gap-2 items-start">
                <span className="text-sm font-bold text-zinc-800">
                  {t("BORROWER.LABELS.INSURANCE_PROJECT")}
                </span>
                <span className="text-sm text-primary font-medium bg-primary/15 px-3 py-1 rounded-full">
                  {insuranceProject && insuranceProject.label}
                </span>
              </div>
            </div>
            {formattedStartDate && (
              <div className="flex items-start gap-4">
                <div className="p-4 border border-primary/50 rounded-xl bg-primary/10">
                  <Calendar className="text-primary w-11 h-11" />
                </div>
                <div className="flex flex-col gap-2 items-start">
                  <span className="text-sm font-bold text-zinc-800">
                    {t("BORROWER.LABELS.PROJECT_START_DATE")}
                  </span>
                  <span className="text-sm text-primary font-medium bg-primary/15 px-3 py-1 rounded-full">
                    {formattedStartDate}
                  </span>
                </div>
              </div>
            )}
          </div>
        </GenericCardInfos>
      )}
    </CardsSection>
  )
}
